import { useState, useEffect, useRef } from "react"
import { CheckIcon } from "@heroicons/react/24/solid"
import { MinusIcon } from "@heroicons/react/24/outline"

export function getWebStoreId(storePrice) {
  if (!storePrice?.real_product_id) return null
  const entry = storePrice.real_product_id.find((r) => {
    const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
    return platform === "Web"
  })
  return entry?.id || null
}

export function getHighlightLabel(feature, plan) {
  if (!feature || typeof feature !== "object") return String(feature ?? "")
  const planId = typeof plan._id === "string" ? plan._id : plan._id
  const entry = feature.plan_values?.find((pv) => {
    const pvId = typeof pv.plan === "string" ? pv.plan : pv.plan?._id
    return pvId === planId
  })
  return entry?.param || feature.name || ""
}

export function getHighlightEntry(item, plan, product, features) {
  if (typeof item === "string") {
    const feature = features?.find((f) => f._id === item)
    if (!feature) return { name: resolveHighlight(item, product), value: null }
    if (!feature.plan_values?.length) return { name: feature.name || item, value: null }
    const param = getHighlightLabel(feature, plan)
    if (param === "✓" || param === "✗") return { name: feature.name, value: null }
    return { name: feature.name, value: param }
  }
  if (item && typeof item === "object" && item.plan_values) {
    const param = getHighlightLabel(item, plan)
    if (param === "✓" || param === "✗") return { name: item.name || param, value: null }
    return { name: item.name, value: param }
  }
  return {
    name: resolveHighlight(item?.text || item?.value || String(item), product),
    value: null,
  }
}

export function getPlanPriceLabel(plan, storeData, billing, texts) {
  if (plan.id === "free") return texts?.free || "Free"
  if (plan.id === "enterprise") return texts?.custom || "Custom"
  const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
  const product = storeData?.products?.[productId]
  const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
  const storePrice = storeData?.prices?.[priceId]
  const byInterval = (i) => storePrice?.price_model?.find((m) => m.phases?.[0]?.interval === i) ?? null
  const monthlyAmount = byInterval("month")?.amount ?? null
  const annualAmount = byInterval("year")?.amount ?? null
  const annualPerMonth = annualAmount != null ? Math.round((annualAmount / 12) * 100) / 100 : null
  const amount = billing === "annual" && annualPerMonth != null ? annualPerMonth : monthlyAmount
  return amount != null ? `$${amount} ${texts?.perMonth || "/ month"}` : ""
}

export function useStuck(offset = 64) {
  const sentinelRef = useRef(null)
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { rootMargin: `-${offset + 1}px 0px 0px 0px`, threshold: 0 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [offset])
  return [sentinelRef, stuck]
}

export function getValue(feature, plan) {
  const planId = typeof plan._id === "string" ? plan._id : plan._id
  const entry = feature.plan_values?.find((pv) => {
    if (!pv.plan) return false
    const pvId = typeof pv.plan === "string" ? pv.plan : pv.plan?._id
    const pvSlug = pv.plan?.id || pv.plan?.content?.id
    return pvId === planId || pvSlug === plan.id
  })
  return entry?.param || "—"
}

export function ValueCell({ value, texts }) {
  if (value === "✓")
    return <CheckIcon className="w-5 h-5 text-success mx-auto" />
  if (value === "✗" || value === "—")
    return <MinusIcon className="w-5 h-5 text-text_secondary/40 mx-auto" />
  if (value === "Unlimited")
    return <span className="text-sm text-text">{texts?.unlimited || value}</span>
  if (value === "Shown")
    return <span className="text-sm text-text">{texts?.shown || value}</span>
  if (value === "Removed")
    return <span className="text-sm text-text">{texts?.removed || value}</span>
  return <span className="text-sm text-text">{value}</span>
}

export function resolveHighlight(text, product) {
  if (!product || typeof text !== "string") return text
  return text.replace(/\{\{([\w.]+)\}\}/g, (match, path) => {
    const parts = path.split(".")
    if (parts[0] !== "product") return match
    let val = product
    for (let i = 1; i < parts.length; i++) {
      if (val == null) return match
      val = val[parts[i]]
    }
    if (val != null) return typeof val === "number" ? val.toLocaleString() : val
    return match
  })
}